import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  WarningAmberOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import DeleteAccount from './DeleteAccount';
import { useAuth } from '../contexts/AuthContext';
import { appVersion } from '../utils/appVersion';
import { authColors, fontStack, avatarGradient } from './auth/authStyles';
import { getJwtPayload } from '../utils/jwt';

const InfoField = ({ label, value }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <Typography sx={{ color: authColors.textPlaceholder, fontSize: '10px', fontWeight: 600, letterSpacing: '1.2px', fontFamily: fontStack }}>
      {label}
    </Typography>
    <Typography sx={{ color: authColors.textPrimary, fontSize: '14px', fontWeight: 500, fontFamily: fontStack, wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const Settings = () => {
  const { user } = useAuth();

  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Utilisateur';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  const token = localStorage.getItem('token');
const tokenPayload = getJwtPayload(token);

const role = String(
  user?.role ||
    tokenPayload?.role ||
    ''
).toLowerCase();

const isAdmin = role === 'admin';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        fontFamily: fontStack,
      }}
    >
      {/* Carte profil */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: '20px',
          p: '22px',
          backgroundColor: authColors.bgCard,
          borderRadius: '16px',
          border: `1px solid ${authColors.borderSubtle}`,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: '999px',
            background: avatarGradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ color: authColors.onAccent, fontSize: '24px', fontWeight: 700, fontFamily: fontStack }}>
            {initials}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Typography sx={{ color: authColors.textPrimary, fontSize: '20px', fontWeight: 700, fontFamily: fontStack }}>
            {fullName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Box
              sx={{
                px: '10px',
                py: '4px',
                borderRadius: '999px',
                backgroundColor: 'rgba(52, 211, 153, 0.13)',
                border: `1px solid ${authColors.accentGreenDark}`,
              }}
            >
              <Typography sx={{ color: authColors.accentGreen, fontSize: '11px', fontWeight: 700, fontFamily: fontStack }}>
                {isAdmin ? 'Admin' : 'Utilisateur'}
              </Typography>
            </Box>
            <Typography sx={{ color: authColors.textMuted, fontSize: '13px', fontFamily: fontStack }}>
              · {user?.email || ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Informations personnelles */}
      <Box
        sx={{
          p: '24px',
          backgroundColor: authColors.bgCard,
          borderRadius: '16px',
          border: `1px solid ${authColors.borderSubtle}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography sx={{ color: authColors.textPrimary, fontSize: '15px', fontWeight: 700, fontFamily: fontStack }}>
            Informations personnelles
          </Typography>
          <Typography sx={{ color: authColors.textPlaceholder, fontSize: '11px', fontFamily: fontStack }}>
            Consultez et gérez vos informations de compte
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: '16px',
          }}
        >
          <InfoField label="PRÉNOM" value={firstName} />
          <InfoField label="NOM" value={lastName} />
          <InfoField label="EMAIL" value={user?.email} />
          <InfoField label="TÉLÉPHONE" value={user?.phoneNumber} />
        </Box>
      </Box>

      {/* Zone sensible */}
      <Box
        sx={{
          p: '24px',
          backgroundColor: '#2E1C24',
          borderRadius: '16px',
          border: '1px solid #7A2430',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '12px',
              backgroundColor: '#4A1B24',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberOutlined sx={{ fontSize: 22, color: authColors.danger }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ color: '#FFB3B3', fontSize: '15px', fontWeight: 700, fontFamily: fontStack }}>
              Zone sensible
            </Typography>
            <Typography sx={{ color: '#D99AA5', fontSize: '12px', fontFamily: fontStack }}>
              Ces actions sont irréversibles. Soyez sûr avant de continuer.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: '14px',
            p: '16px',
            backgroundColor: '#1E1220',
            borderRadius: '12px',
            border: '1px solid #7A2430',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={{ color: authColors.textPrimary, fontSize: '14px', fontWeight: 700, fontFamily: fontStack }}>
              Supprimer mon compte
            </Typography>
            <Typography sx={{ color: authColors.textMuted, fontSize: '12px', fontFamily: fontStack }}>
              Efface définitivement vos données, alertes et permissions.
            </Typography>
          </Box>
          <DeleteAccount />
        </Box>
      </Box>

      {/* À propos */}
      <Box
        sx={{
          p: '16px 20px',
          backgroundColor: authColors.bgCard,
          borderRadius: '16px',
          border: `1px solid ${authColors.borderSubtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <InfoOutlined sx={{ fontSize: 16, color: authColors.accentBlue }} />
        <Typography sx={{ color: authColors.textMuted, fontSize: '12px', fontFamily: fontStack }}>
          Ecowatch 360 · Version {appVersion()} · React, Material-UI, Highcharts, AWS
        </Typography>
      </Box>
    </Box>
  );
};

export default Settings;
